"use client";

import type { PointerEvent as ReactPointerEvent } from "react";
import { cn } from "../../lib";
import type { MediaPlayerSeekPinEnterMode } from "../media-player/use-media-player-seek-pin";
import { BarTargetPin, type BarTargetPinIcon } from "./bar-target-pin";

/** Hover reveal — distinct from scrub/seek appear-in. */
export const MEDIA_PLAYER_SEEK_PIN_FADE_IN_MS_CLASS = "duration-300 ease-out";

interface MediaPlayerSeekPinOverlayProps {
	pinVisible: boolean;
	/** Hover fades in; scrub / seek appear instantly when revealing from hidden. */
	pinEnterMode?: MediaPlayerSeekPinEnterMode;
	showLockIcon: boolean;
	playheadPercent: number;
	interactive: boolean;
	onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerLeave: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
}

export function MediaPlayerSeekPinOverlay({
	pinVisible,
	pinEnterMode = "fade",
	showLockIcon,
	playheadPercent,
	interactive,
	onPointerDown,
	onPointerMove,
	onPointerUp,
	onPointerLeave,
	onPointerCancel,
}: MediaPlayerSeekPinOverlayProps) {
	const seekPinIcon: BarTargetPinIcon = showLockIcon ? "lock" : "book-open";
	const pinInteractive = pinVisible && interactive;

	return (
		<div
			className={cn(
				"absolute bottom-full z-[7] -translate-x-1/2",
				"transition-opacity motion-reduce:transition-none",
				pinVisible
					? pinEnterMode === "fade"
						? `opacity-100 ${MEDIA_PLAYER_SEEK_PIN_FADE_IN_MS_CLASS}`
						: "opacity-100 duration-0"
					: "opacity-0 duration-700 ease-in",
				pinInteractive
					? "pointer-events-auto cursor-grab touch-none select-none active:cursor-grabbing"
					: "pointer-events-none",
			)}
			style={{ left: `${playheadPercent}%` }}
			aria-hidden
			data-testid="media-player-seek-pin"
			data-visible={pinVisible ? "true" : "false"}
			data-enter={pinEnterMode}
			data-lock={showLockIcon ? "true" : "false"}
			data-seek-pin-grab=""
			onPointerDown={
				pinInteractive
					? (event) => {
							event.stopPropagation();
							onPointerDown(event);
						}
					: undefined
			}
			onPointerMove={pinInteractive ? onPointerMove : undefined}
			onPointerUp={pinInteractive ? onPointerUp : undefined}
			onPointerLeave={pinInteractive ? onPointerLeave : undefined}
			onPointerCancel={pinInteractive ? onPointerCancel : undefined}
		>
			<BarTargetPin icon={seekPinIcon} sizePx={28} className="mb-[-2px]" />
		</div>
	);
}
